import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery, useTheme } from '@mui/material'
import { Button, DeleteIcon, Table } from 'components'
import { useBlendingService } from 'services'
import { successToast } from 'utils/toastService'
import { blendsDraftColumns } from './columns'
import {
  SectionCard,
  SectionTitle,
  StyledCardHeader,
  TableContainer,
} from '../../Blends.styled'
import useNewBlendModal from '../../dialogs/useNewBlendModal'
import { PlusIcon } from '../../../../assets/svgComponents/PlusIcon'
import { TBlend } from '../../../../types'
import { CrudAction, Entity } from '../../../../types/permissions'
import { useDeleteModal } from '../../../../components/dialogs'
import { TableActionConfig } from '../../../../components/Table/types'
import { usePagination, usePermissions } from '../../../../utils/hooks'

export const BlendsDraftList = () => {
  const { checkPermission } = usePermissions()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))
  const { getBlends, deleteBlend } = useBlendingService()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const {
    currentPage: blendsDraftListCurrentPage,
    setCurrentPage: setBlendsDraftListCurrentPage,
    rowsPerPage: blendsDraftListRowsPerPage,
    setRowsPerPage: setBlendsDraftListRowsPerPage,
  } = usePagination({ tableKey: 'blendsDraftList' })

  const { data: blendsData } = useQuery({
    queryKey: [
      'blends-draft',
      blendsDraftListCurrentPage,
      blendsDraftListRowsPerPage,
    ],
    queryFn: () =>
      getBlends({
        currentPage: blendsDraftListCurrentPage,
        itemsPerPage: blendsDraftListRowsPerPage,
      }),
    retry: false,
  })

  const [newBlendModal, toggleNewBlendModal] = useNewBlendModal()
  const [deleteBlendId, setDeleteBlendId] = useState<number | null>(null)

  const { mutate: deleteBlendMutation } = useMutation(deleteBlend, {
    onSuccess: (data) => {
      setDeleteBlendId(null)
      void queryClient.invalidateQueries(['blends-draft'])
      successToast(data?.detail ? data.detail : 'Blend deleted successfully')
    },
    onError: (error: { detail?: string }) => {
      toast.error(error?.detail ? error.detail : 'Blend cannot be deleted')
    },
  })

  const [deleteModal, toggleDeleteModal] = useDeleteModal({
    children: 'Are you sure you want to remove this blend?',
    onConfirm: async () => {
      if (deleteBlendId) {
        deleteBlendMutation(deleteBlendId)
      }
    },
  })

  const actions: TableActionConfig<TBlend>[] = [
    {
      name: 'delete',
      icon: <DeleteIcon size={20} />,
      handler: (row: TBlend) => {
        if (!row?.id) return
        setDeleteBlendId(row.id)
        toggleDeleteModal()
      },
      permission: {
        entity: Entity.BLENDING,
        action: CrudAction.DELETE,
      },
    },
  ]

  const handleViewBlendDetails = (row: TBlend) => {
    navigate(`/blends/${row.id}/${row?.product_id}`)
  }

  const onBlendsDraftListPaginationChange = (page: number, perPage: number) => {
    setBlendsDraftListCurrentPage(page)
    setBlendsDraftListRowsPerPage(perPage)
  }

  return (
    <SectionCard>
      <StyledCardHeader>
        <SectionTitle>Blends Draft</SectionTitle>
        <Button
          variantType="text"
          startIcon={<PlusIcon />}
          component="label"
          onClick={toggleNewBlendModal}
          disabled={!checkPermission(Entity.BLENDING, CrudAction.CREATE)}
        >
          New Blend
        </Button>
      </StyledCardHeader>
      <TableContainer
        sx={{
          height: isSmallScreen ? 'unset' : 'calc(100vh - 200px)',
        }}
      >
        <Table
          showHeader={true}
          showPagination={true}
          onPaginationChange={onBlendsDraftListPaginationChange}
          data={blendsData?.results}
          total={blendsData?.pagination?.total || 0}
          columns={blendsDraftColumns(actions)}
          onRowClick={handleViewBlendDetails}
          // tableKey="blendsDraftList"
        />
      </TableContainer>
      {newBlendModal}
      {deleteModal}
    </SectionCard>
  )
}
